import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import PreviewIcon from "../../../../../../../assets/svg/preview-icon";
import RedirectIcon from "../../../../../../../assets/svg/redirect-icon";
import EditIcon from "../../../../../../../assets/svg/edit-icon";
import HashtagIcon from "../../../../../../../assets/svg/hashtag-icon";
import CopyIcon from "../../../../../../../assets/svg/copy-icon";
import TrashIcon from "../../../../../../../assets/svg/trash-icon";
import { customToast } from "../../../../../../../utils/custom-toast/custom-toast";
import { HelpDeskTicket } from "../utils/types";
import { PermissionDisabledWrapper } from "../../PermissionDisabledWrapper";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../../../redux/store";
import { getHelpdeskLabels } from "../../../../../../../utils/constants/mappings";

interface TicketCardActionMenuProps {
  ticket: HelpDeskTicket;
  triggerRef: React.RefObject<HTMLElement>;
  onDeleteClick: () => void;
  onClose: () => void;
  onShare?: () => void;
  hideView?: boolean;
  canUpdate?: boolean;
}

function computePosition(triggerRef: React.RefObject<HTMLElement>) {
  if (!triggerRef.current)
    return {
      x: 0,
      top: 0 as number | undefined,
      bottom: undefined as number | undefined,
    };
  const rect = triggerRef.current.getBoundingClientRect();
  const popupWidth = 160;
  const popupHeight = 310;
  const gap = 4;

  const spaceBelow = window.innerHeight - rect.bottom - gap;
  const openAbove = spaceBelow < popupHeight && rect.top > spaceBelow;

  let x = rect.right - popupWidth;
  if (x < 4) x = 4;

  return {
    x,
    ...(openAbove
      ? { bottom: window.innerHeight - rect.top + gap, top: undefined }
      : { top: rect.bottom + gap, bottom: undefined }),
  };
}

export const TicketCardActionMenu = ({
  ticket,
  triggerRef,
  onDeleteClick,
  onClose,
  onShare,
  hideView,
  canUpdate = true,
}: TicketCardActionMenuProps) => {
  const ticketId = ticket?.ticket_id;
  const navigate = useNavigate();
  const position = computePosition(triggerRef);
  const region = useSelector((state: RootState) => state?.auth?.client?.region);
  const helpdeskLabels = getHelpdeskLabels(region);

  const handleView = () => {
    navigate(`/dashboard/crm/helpdesk/all-tickets/${ticketId}`);
    onClose();
  };

  const handleOpenInNewTab = () => {
    window.open(`/dashboard/crm/helpdesk/all-tickets/${ticketId}`, "_blank");
    onClose();
  };

  const handleEditTicket = () => {
    navigate(`/dashboard/crm/helpdesk/edit-ticket/${ticketId}`);
    onClose();
  };

  const handleCopyId = () => {
    navigator?.clipboard?.writeText(String(ticketId));
    customToast(`${helpdeskLabels.ticketId} copied`, "success");
    onClose();
  };

  const handleCopyLink = () => {
    const link = `${window?.location?.origin}/dashboard/crm/helpdesk/all-tickets/${ticketId}`;
    navigator?.clipboard?.writeText(link);
    customToast(`${helpdeskLabels.ticketSingular} link copied`, "success");
    onClose();
  };

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const menuItems: Array<{
    label: string;
    icon: typeof PreviewIcon;
    onClick: () => void;
    className: string;
    requiresUpdate?: boolean;
  }> = [
    ...(!hideView
      ? [
          {
            label: "View",
            icon: PreviewIcon,
            onClick: handleView,
            className:
              "text-verba-indigo dark:text-verba-indigo-dk h-[18px] w-[18px] shrink-0 mr-[1px]",
          },
        ]
      : []),
    {
      label: "Open in New Tab",
      icon: RedirectIcon,
      onClick: handleOpenInNewTab,
      className:
        "text-verba-indigo dark:text-verba-indigo-dk h-[18px] w-[18px] shrink-0",
    },
    {
      label: helpdeskLabels.editTicket,
      icon: EditIcon,
      onClick: handleEditTicket,
      className:
        "text-verba-indigo dark:text-verba-indigo-dk h-[19px] w-[19px] shrink-0 mb-0.5",
      requiresUpdate: true,
    },
    {
      label: "Copy ID",
      icon: HashtagIcon,
      onClick: handleCopyId,
      className: "text-verba-indigo dark:text-verba-indigo-dk h-5 w-5 shrink-0",
    },
    {
      label: "Copy Link",
      icon: CopyIcon,
      onClick: handleCopyLink,
      className:
        "text-verba-indigo dark:text-verba-indigo-dk h-4 w-4 shrink-0 ml-0.5",
    },
  ];

  return createPortal(
    <>
      <div
        className="fixed inset-0 overflow-hidden"
        style={{ zIndex: 9999998 }}
        onClick={(e) => {
          e?.stopPropagation();
          onClose();
        }}
        onWheel={(e) => e?.preventDefault()}
        onTouchMove={(e) => e?.preventDefault()}
      />
      <div
        data-popup-content
        className="fixed w-[160px] bg-popper-bg-fixed-white dark:bg-popper-bg-fixed-white-dk border border-border-gray dark:border-border-gray-dk rounded-lg shadow-popper-blur-effect backdrop-blur-[20px] overflow-hidden"
        style={{
          left: position.x,
          ...(position.bottom !== undefined
            ? { bottom: position.bottom }
            : { top: position.top }),
          zIndex: 9999999,
        }}
      >
        <div className="flex flex-col py-3 px-3 gap-0">
          {menuItems?.map((item) => {
            const itemDisabled = !canUpdate && item?.requiresUpdate === true;
            return (
              <PermissionDisabledWrapper
                key={item?.label}
                disabled={itemDisabled}
                className="w-max"
                tooltipSide="left"
              >
                <button
                  onClick={item?.onClick}
                  className="flex items-center gap-1 px-2 py-2 rounded-lg w-full hover:bg-hover-effect dark:hover:bg-hover-effect-dk transition-colors cursor-pointer"
                >
                  <item.icon className={`${item?.className}`} />
                  <span className="text-md font-normal text-heading-text dark:text-heading-text-dk whitespace-nowrap">
                    {item?.label}
                  </span>
                </button>
              </PermissionDisabledWrapper>
            );
          })}
        </div>

        <div className="h-px bg-border-gray dark:bg-border-gray-dk mx-3" />

        <div className="px-3 py-3">
          <PermissionDisabledWrapper disabled={!canUpdate} className="w-max" tooltipSide="left">
            <button
              onClick={onDeleteClick}
              className="flex items-center gap-1 px-2 py-2 rounded-lg w-full hover:bg-hover-effect dark:hover:bg-hover-effect-dk transition-colors cursor-pointer"
            >
              <TrashIcon className="text-verba-red-alert dark:text-verba-red-alert-dk h-[18px] w-[18px] shrink-0" />
              <span className="text-md font-normal text-verba-red-alert dark:text-verba-red-alert-dk whitespace-nowrap mt-0.5">
                Delete
              </span>
            </button>
          </PermissionDisabledWrapper>
        </div>
      </div>
    </>,
    document.body
  );
};
